import React, { useEffect, useState } from "react";
import "../../../scss/Login.scss";
import { Link, useNavigate } from "react-router-dom";
import {
  customMessage,
  encrypt,
  getCurrentUser,
  showToastTC,
  showToastTR,
} from "../../../Utils/BaseApp";
import * as DomicilappService from "../../../services/DomicilappService";
import "react-toastify/dist/ReactToastify.css";
import {
  CButton,
  CCol,
  CContainer,
  CForm,
  CFormCheck,
  CFormInput,
  CImage,
  CRow,
} from "@coreui/react-pro";
import logoFull from "../../../assets/icons/logo-full.png";
import { AuthPostModel } from "../../../models/Auth";
import { isUserLogin, saveLogin, saveLoginUser } from "../../../Utils/Auth";
import { useAppDispatch } from "../../../hooks/hooks";
import { startLoading, stopLoading } from "../../../redux/features/loadSlice";
import { useSeo } from "../../../hooks/useSeo";

export interface LoginProps { }

export const Login: React.FC<LoginProps> = ({ }: LoginProps) => {
  useSeo({
    title: "Iniciar sesión | Portal para negocios DomicilApp",
    description: "Ingresa al portal de DomicilApp para recibir los pedidos de tu negocio y administrar tu catálogo, horarios y promociones.",
    index: true,
  });
  const [formError, setFormError] = useState(false);
  const [login, setLogin] = useState<AuthPostModel | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isUserLogin()) {
      navigate("/dashboard");
    }
    // Motivo del cierre de sesión (ej. se cerró desde otro dispositivo), guardado antes de redirigir
    const logoutReason = sessionStorage.getItem("logout_reason");
    if (logoutReason) {
      sessionStorage.removeItem("logout_reason");
      showToastTC(logoutReason, 4000, "info");
    }
  }, []);

  useEffect(() => {
    if (login) {
      dispatch(stopLoading());
      if (login.status !== 200) {
        showToastTC(customMessage(login.code_app), 3000, "error");
      } else {
        showToastTR(customMessage(login.code_app), 1000, "success");
        saveLogin(login.data.access_token);
        getCurrentUser().then((currentUser) => {
          if (currentUser !== null) {
            saveLoginUser(currentUser);
            const redirect_path = sessionStorage.getItem("redirect_path");
            const path = redirect_path || "/dashboard";
            sessionStorage.removeItem("redirect_path");
            navigate(path);
          }
        });
      }
    }
  }, [login]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    var { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    if (!form.checkValidity()) {
      form.reportValidity();
      setFormError(true);
      return;
    }
    dispatch(startLoading());

    try {
      const body = {
        email: await encrypt(formData.email),
        password: await encrypt(formData.password),
      };

      const response = await DomicilappService.login(body);
      setLogin(response);
    } catch (error) {
      console.error("Error al llamar a la API:", error);
    }
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={10} lg={8} className="p-0">
            <div className="card-login border-0 shadow-lg rounded-4 overflow-hidden">
              <CRow className="g-0">
                {/* Lateral Izquierdo - Branding Elegante */}
                <CCol md={5} className="login-banner p-4 p-lg-5 d-flex flex-column justify-content-between text-white">
                  <div>
                    <Link to="/" className="d-inline-block bg-white bg-opacity-10 backdrop-blur p-2 rounded-3 mb-4">
                      <CImage src={logoFull} height={32} alt="DomicilApp Logo" className="rounded" />
                    </Link>
                  </div>
                  <div className="my-auto py-4">
                    <span className="badge bg-white bg-opacity-20 text-black rounded-pill px-3 py-2 mb-3 fw-normal fs-7">
                      Panel Administrativo
                    </span>
                    <h2 className="fw-bold mb-2 display-6 text-white">¡Hola de nuevo!</h2>
                    <p className="text-white-50 fs-6">
                      Gestiona tus órdenes, comercios y repartos de manera ágil y centralizada.
                    </p>
                  </div>
                  <div className="footer-brand pt-3 border-top border-white border-opacity-10 fs-7 text-white-50">
                    &copy; DomicilApp ·{" "}
                    <a href="https://www.domicilapp.com" className="text-white-50">www.domicilapp.com</a>
                  </div>
                </CCol>

                {/* Lateral Derecho - Formulario Clean */}
                <CCol md={7} className="bg-white p-4 p-lg-5 d-flex flex-column justify-content-center">
                  <div className="mb-4">
                    <h1 className="h3 fw-bold text-dark mb-1">Iniciar Sesión</h1>
                    <p className="text-muted fs-7">Ingresa tus credenciales para continuar</p>
                  </div>

                  <CForm
                    className="needs-validation"
                    noValidate
                    validated={formError}
                    onSubmit={handleSubmit}
                  >
                    <div className="mb-3">
                      <label className="form-label text-secondary fw-medium fs-7">Correo Electrónico</label>
                      <CFormInput
                        type="email"
                        name="email"
                        placeholder="ejemplo@domicilapp.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="custom-input py-2 px-3"
                        feedbackInvalid="Escribe un correo electrónico válido."
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label text-secondary fw-medium fs-7">Contraseña</label>
                      <CFormInput
                        type="password"
                        name="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="custom-input py-2 px-3"
                        feedbackInvalid="Escribe una contraseña válida."
                        required
                      />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4 fs-7">
                      <CFormCheck
                        id="checkSaveSource"
                        type="checkbox"
                        label={<span className="text-secondary">Mantener sesión activa</span>}
                      />
                      <Link className="text-decoration-none fw-semibold text-danger" to="/signup">
                        ¿No tienes cuenta?
                      </Link>
                    </div>

                    <CButton
                      type="submit"
                      className="btn-submit w-100 py-2 fw-semibold rounded-3 border-0"
                    >
                      Ingresar al Dashboard
                    </CButton>
                  </CForm>
                </CCol>
              </CRow>
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};